import { useEffect, useState } from 'react';

type ObjectUrlCacheEntry = {
  url: string;
  refs: number;
  revokeTimer: number | null;
};

const objectUrlCache = new WeakMap<File, ObjectUrlCacheEntry>();

const getOrCreateObjectUrl = (file: File): string => {
  let entry = objectUrlCache.get(file);
  if (!entry) {
    entry = { url: URL.createObjectURL(file), refs: 0, revokeTimer: null };
    objectUrlCache.set(file, entry);
  }
  if (entry.revokeTimer != null) {
    window.clearTimeout(entry.revokeTimer);
    entry.revokeTimer = null;
  }
  return entry.url;
};

const revokeObjectUrlLater = (file: File, entry: ObjectUrlCacheEntry): void => {
  if (entry.revokeTimer != null) return;
  entry.revokeTimer = window.setTimeout(() => {
    entry.revokeTimer = null;
    if (entry.refs > 0) return;
    if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
      URL.revokeObjectURL(entry.url);
    }
    objectUrlCache.delete(file);
  }, 0);
};

/**
 * 把 File 转成 objectURL 字符串；string 直接返回（已是 URL）；null 返回空串。
 *
 * 关键：URL 按 File identity 做模块级缓存，File 引用不变 → URL 不变 →
 * <img>/<video> src 不变 → 浏览器复用解码缓存，不会"闪一下"。
 *
 * 卸载或 file 变化时延迟 revoke，避免 React StrictMode 的挂载探测把仍在使用的
 * blob URL 提前释放，导致控制台出现 blob:... ERR_FILE_NOT_FOUND。
 *
 * 适用场景：UploadChip 的图片预览。
 */
export function useObjectURL(file: File | string | null | undefined): string {
  const resolveUrl = (): string => {
    if (file == null) return '';
    if (typeof file === 'string') return file;
    if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return '';
    return getOrCreateObjectUrl(file);
  };
  const [url, setUrl] = useState(resolveUrl);

  useEffect(() => {
    if (file == null) {
      setUrl('');
      return;
    }
    if (typeof file === 'string') {
      setUrl(file);
      return;
    }
    if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
      setUrl('');
      return;
    }

    getOrCreateObjectUrl(file);
    const entry = objectUrlCache.get(file)!;
    entry.refs += 1;
    setUrl(entry.url);

    return () => {
      entry.refs = Math.max(0, entry.refs - 1);
      revokeObjectUrlLater(file, entry);
    };
  }, [file]);

  return url;
}
